import styled from "@emotion/styled";
import React, { useContext } from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import { AuthorProps } from "@brexhq/next/src/components/shared/AuthorList/AuthorList";
import { BynderImage } from "@brexhq/next/src/components/shared/BynderAsset/Image/BynderImage";
import { ArticleContext } from "@brexhq/next/src/contexts/ArticleContext";
import { BodyL, BodyM } from "@brexhq/shared/components/Typography/Components";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";
import { SanityArticleBylineProps } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems";

import { Link } from "../../shared/Link/Link";

export interface Props
  extends Omit<SanityArticleBylineProps, "_key" | "_type"> {
  theme: ModuleThemeType;
}

interface ViewProps extends Props {
  readTime: number;
  smallMarginTop?: boolean;
  articleDate?: string;
  author?: AuthorProps;
  linkLabel?: string;
}

export const DividerDotContainer = styled(BodyL)(
  ({ theme: { breakpoints } }) => ({
    [breakpoints.MED]: {
      display: "none",
    },
  }),
);

export function ArticleBylineView({
  showAuthor,
  showDate,
  showReadTime,
  showLink,
  theme,
  articleDate,
  author,
  readTime,
  smallMarginTop,
  linkLabel,
  url,
}: ViewProps) {
  const {
    standard: { accent, textSecondary },
  } = getModuleThemeColors(theme);
  const shouldShowReadTime = readTime && showReadTime;
  const shouldShowDate = articleDate && showDate;
  const showFirstDivider = showAuthor && (shouldShowDate || shouldShowReadTime);
  const shouldShowLink = showLink && linkLabel;
  const showSecondDivider = shouldShowDate && shouldShowReadTime;

  const HeadShot = styled(BynderImage)({
    clipPath: "circle()",
    width: 32,
    height: 32,
    objectFit: "cover",
    maxWidth: "64px",
  });
  return (
    <Container
      direction={"row"}
      gap="xsml"
      alignItems={{ LRG: "center", MED: "flex-start", SML: "flex-start" }}
      margin={smallMarginTop ? ["xsml", 0, 0, 0] : ["lrg", 0, 0, 0]}
    >
      {showAuthor && author && (
        <HeadShot
          image={author.headshot.image}
          altTextOverride={`headshot photo of ${author.name}`}
        />
      )}

      <Container
        direction={{ LRG: "row", MED: "column", SML: "column" }}
        gap={"xxsml"}
        alignItems={{ LRG: "center", MED: "flex-start", SML: "flex-start" }}
      >
        {showAuthor && author && (
          <BodyM color={textSecondary}>{author.name}</BodyM>
        )}

        {showFirstDivider && (
          <DividerDotContainer color={accent} aria-hidden="true">
            &middot;
          </DividerDotContainer>
        )}

        <Container
          direction={"row"}
          gap={{ LRG: "xxsml", MED: "none", SML: "none" }}
          alignItems={"center"}
          display={{ LRG: "none", MED: "flex", SML: "flex" }}
        >
          <BodyM color={textSecondary}>
            {shouldShowDate && `${articleDate}`}
            {/*  only show comma if both are present */}
            {shouldShowReadTime && shouldShowDate && ", "}
            {shouldShowReadTime && `${readTime} min read`}
          </BodyM>
        </Container>

        <Container
          direction={"row"}
          gap={{ LRG: "xxsml", MED: "none", SML: "none" }}
          alignItems={"center"}
          display={{ LRG: "flex", MED: "none", SML: "none" }}
        >
          {shouldShowDate && articleDate && (
            <BodyM color={textSecondary}>{articleDate}</BodyM>
          )}

          {showSecondDivider && (
            <DividerDotContainer color={accent} aria-hidden="true">
              &middot;
            </DividerDotContainer>
          )}

          {shouldShowReadTime && (
            <BodyM color={textSecondary}>{readTime} min read</BodyM>
          )}
          {shouldShowLink && (
            <Link
              modifier="arrow"
              to={url ?? ""}
              label={linkLabel ?? "Read More"}
              component="ArticleLinkList"
              theme={theme}
              color={accent}
              size={{ LRG: "lrg", MED: "lrg", SML: "lrg" }}
            />
          )}
        </Container>
      </Container>
    </Container>
  );
}

/**
 * Option in Block module.
 * Shows article author, date, and read time.
 */
export const ArticleByline = (props: Props) => {
  const { articleDate, authors, readTime } = useContext(ArticleContext);
  const author = authors ? authors[0] : undefined;

  return (
    <ArticleBylineView
      {...props}
      author={author}
      articleDate={articleDate}
      readTime={readTime}
    />
  );
};
